import {
  Switch,
  useToast,
  useColorModeValue,
  SimpleGrid,
} from "@chakra-ui/react";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Store,
  StoreStatus,
  StoreConfig,
  StoreLanguage,
  VTemplate,
} from "../../../entity/Store";
import { WSStore } from "../../../services/remote/store/WSStore";
import { useDropzone } from "react-dropzone";
import { selectStore } from "../../../store/selectors/StoreSelector";
import { useSelector } from "react-redux";
import { WSMedia } from "../../../services/remote/media/WSMedia";
import { ThemeTemplate } from "../../../utils/data/ThemeTemplate";
import { ThemeCard } from "./components/ThemeCard";
import {
  FiLayout,
  FiUser,
  FiSliders,
  FiImage,
  FiGlobe,
  FiPower,
  FiHeadphones,
  FiUploadCloud,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiLock,
  FiSave,
  FiRotateCcw,
  FiAlertCircle,
} from "react-icons/fi";
import "./Settings.css";

interface StoreProfileProps {
  storeId?: string;
}

type ImageField = "logo" | "bannerImage" | "bannerImageMobile";

/* =========================
   Reusable building blocks
========================= */

const SettingSection = ({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <section className="setting-card">
    <header className="setting-card-header">
      <div className="setting-card-icon">{icon}</div>
      <div>
        <h3 className="setting-card-title">{title}</h3>
        {description && (
          <p className="setting-card-description">{description}</p>
        )}
      </div>
    </header>
    <div className="setting-card-body">{children}</div>
  </section>
);

const SettingField = ({
  label,
  htmlFor,
  readOnly,
  children,
}: {
  label: string;
  htmlFor?: string;
  readOnly?: boolean;
  children: React.ReactNode;
}) => (
  <div className="setting-field">
    <label className="setting-label" htmlFor={htmlFor}>
      {label}
      {readOnly && <FiLock size={12} className="setting-label-lock" />}
    </label>
    {children}
  </div>
);

const SettingImageUploader = ({
  label,
  hint,
  alt,
  imageUrl,
  isUploading,
  variant,
  onUpload,
  onRemove,
}: {
  label: string;
  hint?: string;
  alt: string;
  imageUrl?: string;
  isUploading: boolean;
  variant: "logo" | "banner";
  onUpload: (file: File) => void;
  onRemove: () => void;
}) => {
  const { t } = useTranslation();
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"] },
    maxFiles: 1,
    onDrop: (files) => {
      if (files[0]) onUpload(files[0]);
    },
  });

  return (
    <div className="setting-field">
      <label className="setting-label">{label}</label>
      {hint && <span className="setting-hint">{hint}</span>}

      {isUploading ? (
        <div className={`setting-upload-loading setting-upload-${variant}`}>
          <span className="setting-spinner" />
          <span>{t("common.uploading")}</span>
        </div>
      ) : imageUrl ? (
        <div className={`setting-preview setting-preview-${variant}`}>
          <img src={imageUrl} alt={alt} />
          <div className="setting-preview-overlay">
            <button
              type="button"
              className="setting-remove-button"
              onClick={onRemove}
              aria-label={`Remove ${alt}`}
            >
              <FiTrash2 size={16} />
              {t("common.remove", "Remove")}
            </button>
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`setting-upload setting-upload-${variant} ${
            isDragActive ? "setting-upload-active" : ""
          }`}
        >
          <input {...getInputProps()} />
          <div className="setting-upload-icon">
            <FiUploadCloud size={22} />
          </div>
          <p className="setting-upload-title">
            {isDragActive
              ? t("common.dropImageHere")
              : t("common.dragDropImage")}
          </p>
          <p className="setting-upload-hint">
            {t("common.supportedImageFormats")}
          </p>
        </div>
      )}
    </div>
  );
};

/* =========================
   Screen
========================= */

const Settings: React.FC<StoreProfileProps> = ({ storeId }) => {
  const store = useSelector(selectStore);
  const [storeData, setStore] = useState<Store | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<Store>>({});
  const [storeConfig, setStoreConfig] = useState<StoreConfig>({});
  const [snapshot, setSnapshot] = useState("");
  const [uploading, setUploading] = useState<Record<ImageField, boolean>>({
    logo: false,
    bannerImage: false,
    bannerImageMobile: false,
  });
  const [showPassword, setShowPassword] = useState(false);

  const { t } = useTranslation();
  const toast = useToast();
  const switchTrackBg = useColorModeValue("gray.300", "gray.600");

  const isEnabled = formData.status === StoreStatus.ENABLED;

  const isDirty = useMemo(
    () =>
      snapshot !== "" && JSON.stringify({ formData, storeConfig }) !== snapshot,
    [formData, storeConfig, snapshot],
  );

  useEffect(() => {
    loadStoreData();
  }, [storeId]);

  /* ---------- data ---------- */

  const applyStore = (data: Store) => {
    const nextForm: Partial<Store> = {
      name: data.name,
      email: data.email,
      address: data.address,
      phoneNumber: data.phoneNumber,
      subdomain: data.subdomain,
      status: data.status,
    };
    const nextConfig = data.storeConfig || {};
    setStore(data);
    setFormData(nextForm);
    setStoreConfig(nextConfig);
    setSnapshot(
      JSON.stringify({ formData: nextForm, storeConfig: nextConfig }),
    );
  };

  const showError = (title: string, error: unknown) =>
    toast({
      title,
      description: error instanceof Error ? error.message : "Unknown error",
      status: "error",
      duration: 5000,
      isClosable: true,
    });

  const loadStoreData = async () => {
    setIsLoading(true);
    try {
      const data = await WSStore.getInstance().getReleatedStoreByEmail(
        store?.email || "",
      );
      if (data) applyStore(data);
    } catch (error) {
      console.error("Failed to load store data:", error);
      showError(t("error.loadFailed"), error);
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------- handlers ---------- */

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const setConfigValue = (name: keyof StoreConfig, value: string) =>
    setStoreConfig((prev) => ({ ...prev, [name]: value }));

  const handleConfigChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setConfigValue(e.target.name as keyof StoreConfig, e.target.value);

  const handleStatusChange = (isChecked: boolean) =>
    setFormData((prev) => ({
      ...prev,
      status: isChecked ? StoreStatus.ENABLED : StoreStatus.DISABLED,
    }));

  const handleThemeSelect = (templateId: VTemplate) =>
    setStoreConfig((prev) => ({ ...prev, template: templateId }));

  const uploadImage = async (field: ImageField, file: File) => {
    setUploading((prev) => ({ ...prev, [field]: true }));
    try {
      const mediaService = WSMedia.getInstance();
      const s3Url = await mediaService.getGeneratedURL();
      const imageUrl =
        field === "logo"
          ? await mediaService.saveImage(s3Url, file)
          : await mediaService.saveImage(s3Url, file, false);
      setStoreConfig((prev) => ({ ...prev, [field]: imageUrl }));
    } catch (error) {
      console.error(`Failed to upload ${field}:`, error);
      showError(t("error.uploadFailed"), error);
    } finally {
      setUploading((prev) => ({ ...prev, [field]: false }));
    }
  };

  const removeImage = async (field: ImageField) => {
    const url = storeConfig[field];
    if (!url) return;
    try {
      await WSMedia.getInstance().deleteImage([url]);
      setStoreConfig((prev) => ({ ...prev, [field]: undefined }));
    } catch (error) {
      console.error(`Failed to delete ${field}:`, error);
      showError(t("error.deleteFailed"), error);
    }
  };

  const handleSave = async () => {
    if (!store) return;

    setIsSaving(true);
    try {
      const updatedStore: Store = {
        ...store,
        ...formData,
        storeConfig,
        updatedAt: new Date(),
      };

      await WSStore.getInstance().updateStore(updatedStore);

      toast({
        title: t("store.updateSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      await loadStoreData();
    } catch (error) {
      console.error("Failed to update store:", error);
      showError(t("store.updateError"), error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    if (storeData) applyStore(storeData);
  };

  /* ---------- states ---------- */

  if (isLoading && !storeData) {
    return (
      <div className="setting-state">
        <span className="setting-spinner setting-spinner-lg" />
        <p>{t("common.loading", "Loading store data...")}</p>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="setting-state">
        <div className="setting-state-icon">
          <FiAlertCircle size={28} />
        </div>
        <h3>{t("store.notFound", "Store not found")}</h3>
      </div>
    );
  }

  /* ---------- render ---------- */

  return (
    <div className="setting-container">
      {/* Header */}
      <div className="setting-header">
        <div>
          <h2 className="setting-title">{t("sidebar.settings", "Settings")}</h2>
          <p className="setting-subtitle">
            {t(
              "store.settingsSubtitle",
              "Manage your store profile, branding and configuration.",
            )}
          </p>
        </div>
        <span
          className={`setting-status-pill ${
            isEnabled ? "setting-status-on" : "setting-status-off"
          }`}
        >
          <span className="setting-status-dot" />
          {isEnabled ? t("store.enabled") : t("store.disabled")}
        </span>
      </div>

      {/* Theme */}
      <SettingSection
        icon={<FiLayout size={18} />}
        title={t("store.storeTheme")}
        description={t(
          "store.storeThemeDescription",
          "Choose how your storefront looks.",
        )}
      >
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          {ThemeTemplate.map((theme) => (
            <ThemeCard
              key={theme.id}
              theme={theme}
              onApply={() => handleThemeSelect(theme.key as VTemplate)}
              isSelected={storeConfig.template === theme.key}
            />
          ))}
        </SimpleGrid>
      </SettingSection>

      {/* Basic information */}
      <SettingSection
        icon={<FiUser size={18} />}
        title={t("store.basicInformation")}
      >
        <div className="setting-grid">
          <SettingField
            label={t("store.storeName")}
            htmlFor="setting-name"
            readOnly
          >
            <input
              id="setting-name"
              name="name"
              className="setting-input"
              value={formData.name || ""}
              onChange={handleInputChange}
              readOnly
            />
          </SettingField>

          <SettingField
            label={t("store.email")}
            htmlFor="setting-email"
            readOnly
          >
            <input
              id="setting-email"
              name="email"
              type="email"
              className="setting-input"
              value={formData.email || ""}
              onChange={handleInputChange}
              readOnly
            />
          </SettingField>

          <SettingField label={t("store.address")} htmlFor="setting-address">
            <input
              id="setting-address"
              name="address"
              className="setting-input"
              value={formData.address || ""}
              onChange={handleInputChange}
              placeholder={t("store.addressPlaceholder")}
            />
          </SettingField>

          <SettingField label={t("store.phoneNumber")} htmlFor="setting-phone">
            <input
              id="setting-phone"
              name="phoneNumber"
              type="tel"
              className="setting-input"
              value={formData.phoneNumber || ""}
              onChange={handleInputChange}
              placeholder={t("store.phonePlaceholder")}
            />
          </SettingField>

          <SettingField
            label={t("store.subdomain")}
            htmlFor="setting-subdomain"
            readOnly
          >
            <input
              id="setting-subdomain"
              name="subdomain"
              className="setting-input"
              value={formData.subdomain || ""}
              onChange={handleInputChange}
              readOnly
            />
          </SettingField>
        </div>
      </SettingSection>

      {/* Branding */}
      <SettingSection
        icon={<FiImage size={18} />}
        title={t("store.branding", "Branding")}
        description={t(
          "store.brandingDescription",
          "Your logo and banners appear on your storefront.",
        )}
      >
        <div className="setting-branding">
          <SettingImageUploader
            label={t("store.logo")}
            alt="Store logo"
            variant="logo"
            imageUrl={storeConfig.logo}
            isUploading={uploading.logo}
            onUpload={(file) => uploadImage("logo", file)}
            onRemove={() => removeImage("logo")}
          />

          <div className="setting-grid">
            <SettingImageUploader
              label={t("store.bannerImageForDesktop")}
              alt="Desktop banner"
              variant="banner"
              imageUrl={storeConfig.bannerImage}
              isUploading={uploading.bannerImage}
              onUpload={(file) => uploadImage("bannerImage", file)}
              onRemove={() => removeImage("bannerImage")}
            />
            <SettingImageUploader
              label={t("store.bannerImageMobile")}
              alt="Mobile banner"
              variant="banner"
              imageUrl={storeConfig.bannerImageMobile}
              isUploading={uploading.bannerImageMobile}
              onUpload={(file) => uploadImage("bannerImageMobile", file)}
              onRemove={() => removeImage("bannerImageMobile")}
            />
          </div>
        </div>
      </SettingSection>

      {/* Configuration */}
      <SettingSection
        icon={<FiSliders size={18} />}
        title={t("store.storeConfiguration")}
      >
        <div className="setting-grid">
          <SettingField
            label={t("store.facebookPixelId")}
            htmlFor="setting-pixel"
          >
            <input
              id="setting-pixel"
              name="facebookPixelId"
              className="setting-input"
              value={storeConfig.facebookPixelId || ""}
              onChange={handleConfigChange}
              placeholder="Enter Facebook Pixel ID"
            />
          </SettingField>

          <SettingField
            label={t("store.facebookAccessToken")}
            htmlFor="setting-token"
          >
            <div className="setting-input-group">
              <input
                id="setting-token"
                name="facebookAccessToken"
                className="setting-input setting-input-with-action"
                type={showPassword ? "text" : "password"}
                value={storeConfig.facebookAccessToken || ""}
                onChange={handleConfigChange}
                placeholder="Enter Facebook Access Token"
                autoComplete="off"
              />
              <button
                type="button"
                className="setting-input-action"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide token" : "Show token"}
              >
                {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>
          </SettingField>
        </div>
      </SettingSection>

      {/* Language */}
      <SettingSection icon={<FiGlobe size={18} />} title={t("store.language")}>
        <div className="setting-grid">
          <SettingField label={t("store.language")} htmlFor="setting-language">
            <select
              id="setting-language"
              className="setting-input setting-select"
              value={storeConfig.language || StoreLanguage.EN}
              onChange={(e) => setConfigValue("language", e.target.value)}
            >
              <option value={StoreLanguage.EN}>{t("store.english")}</option>
              <option value={StoreLanguage.AR}>{t("store.arabic")}</option>
              <option value={StoreLanguage.FR}>{t("store.french")}</option>
            </select>
          </SettingField>
        </div>
      </SettingSection>

      {/* Status */}
      <SettingSection
        icon={<FiPower size={18} />}
        title={t("store.storeStatus")}
      >
        <div
          className={`setting-status-row ${
            isEnabled ? "setting-status-row-on" : ""
          }`}
        >
          <div className="setting-status-text">
            <label htmlFor="setting-status" className="setting-status-label">
              {t("store.enableStore")}
            </label>
            <span className="setting-status-state">
              {isEnabled ? t("store.enabled") : t("store.disabled")}
            </span>
            <p className="setting-status-description">
              {isEnabled
                ? t("store.enabledDescription")
                : t("store.disabledDescription")}
            </p>
          </div>
          <Switch
            id="setting-status"
            isChecked={isEnabled}
            onChange={(e) => handleStatusChange(e.target.checked)}
            colorScheme="green"
            size="lg"
            sx={{
              "span.chakra-switch__track:not([data-checked])": {
                bg: switchTrackBg,
              },
            }}
          />
        </div>
      </SettingSection>

      {/* Support */}
      <SettingSection
        icon={<FiHeadphones size={18} />}
        title={t("store.supportContact")}
      >
        <div className="setting-grid">
          <SettingField
            label={t("store.supportContact")}
            htmlFor="setting-support-email"
          >
            <input
              id="setting-support-email"
              name="emailForSupport"
              type="email"
              className="setting-input"
              value={storeConfig.emailForSupport || ""}
              onChange={handleConfigChange}
            />
          </SettingField>

          <SettingField
            label={t("store.supportPhone")}
            htmlFor="setting-support-phone"
          >
            <input
              id="setting-support-phone"
              name="phoneNumberForSupport"
              type="tel"
              className="setting-input"
              value={storeConfig.phoneNumberForSupport || ""}
              onChange={handleConfigChange}
            />
          </SettingField>
        </div>
      </SettingSection>

      {/* Sticky save bar */}
      <div
        className={`setting-actions ${isDirty ? "setting-actions-dirty" : ""}`}
      >
        <span className="setting-actions-message">
          {isDirty ? (
            <>
              <span className="setting-dirty-dot" />
              {t("common.unsavedChanges", "You have unsaved changes")}
            </>
          ) : (
            t("common.allChangesSaved", "All changes saved")
          )}
        </span>

        <div className="setting-actions-buttons">
          <button
            type="button"
            className="setting-secondary-button"
            onClick={handleDiscard}
            disabled={!isDirty || isSaving}
          >
            <FiRotateCcw size={15} />
            {t("common.discard", "Discard")}
          </button>
          <button
            type="button"
            className="setting-save-button"
            onClick={handleSave}
            disabled={!isDirty || isSaving}
          >
            {isSaving ? (
              <span className="setting-spinner setting-spinner-light" />
            ) : (
              <FiSave size={16} />
            )}
            {t("common.saveChanges")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
